import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Heart, 
  MoreHorizontal,
  Shirt,
  Star,
  Edit3,
  Trash2,
  Eye,
  Palette,
  Calendar,
  Tag,
  Users,
  Sparkles,
  ChevronDown,
  Check,
  Download
} from 'lucide-react';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useWallet } from '../contexts/WalletContext';
import { supabase } from '../lib/supabase';
import OutfitPreview from '../components/OutfitPreview';
import OutfitDetailModal from '../components/OutfitDetailModal';
import WalletRequired from '../components/WalletRequired';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';

const Outfits = () => {
  const navigate = useNavigate();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { publicKey, isConnected } = useWallet();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [outfitDetailModal, setOutfitDetailModal] = useState({ isOpen: false, outfit: null });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, outfitId: null, outfitName: '' });
  const [downloadNotification, setDownloadNotification] = useState({ isVisible: false, message: '', type: 'success' });
  const dropdownRef = useRef(null);
  const [ownedPieceIds, setOwnedPieceIds] = useState(new Set());

  // Get outfits from localStorage or use empty array
  const [outfits, setOutfits] = useState(() => {
    try {
      const saved = localStorage.getItem('outfits');
      if (saved) {
        const parsedOutfits = JSON.parse(saved);
        
        // Debug: Log the loaded outfit data
        console.log('Outfits loaded from localStorage:', parsedOutfits);
        parsedOutfits.forEach((outfit, index) => {
          console.log(`Outfit ${index + 1}:`, {
            id: outfit.id,
            name: outfit.name,
            imageLength: outfit.image?.length,
            imagePreview: outfit.image?.substring(0, 100),
            hasImage: !!outfit.image
          });
        });
        
        // Check if these are old sample outfits (they won't have screenshots)
        const hasOldSampleData = parsedOutfits.some(outfit => 
          outfit.image === '/images/outsera_purple_logo-nobackground.png' && 
          (outfit.id === '1' || outfit.id === '2' || outfit.id === '3' || outfit.id === '4')
        );
        
        // If old sample data exists, clear it and return empty array
        if (hasOldSampleData) {
          console.log('Clearing old sample outfit data to make room for real outfits');
          localStorage.removeItem('outfits');
          return [];
        }
        
        return parsedOutfits;
      }
      
      // If no saved outfits, return empty array (no more sample data)
      return [];
    } catch (error) {
      console.error('Error loading outfits:', error);
      return [];
    }
  });


  // Fetch owned pieces to validate outfits
  useEffect(() => {
    const fetchOwnedPieces = async () => {
      if (!publicKey || !isConnected) {
        setOwnedPieceIds(new Set());
        return;
      }

      try {
        console.log('🔍 Fetching owned pieces to validate outfits...');
        
        const { data: pieces, error } = await supabase
          .from('pieces')
          .select('id')
          .eq('holder_wallet', publicKey.toString())
          .not('mint_address', 'is', null);

        if (error) {
          console.error('❌ Error fetching owned pieces:', error);
          return;
        }

        const pieceIds = new Set(pieces.map(p => p.id));
        console.log(`✅ Found ${pieceIds.size} owned pieces`);
        setOwnedPieceIds(pieceIds);
      } catch (error) {
        console.error('❌ Error fetching owned pieces:', error);
      }
    };

    fetchOwnedPieces();
  }, [publicKey, isConnected]);

  // Validate outfits and remove sold pieces
  useEffect(() => {
    if (ownedPieceIds.size === 0 || !isConnected) {
      return;
    }

    // Get current outfits from localStorage to avoid stale state
    const savedOutfits = localStorage.getItem('outfits');
    if (!savedOutfits) return;

    try {
      const currentOutfits = JSON.parse(savedOutfits);
      let outfitsUpdated = false;

      const validatedOutfits = currentOutfits.map(outfit => {
        if (!outfit.models || outfit.models.length === 0) {
          return outfit;
        }

        // Filter out pieces that are no longer owned
        const validModels = outfit.models.filter(model => {
          const isOwned = ownedPieceIds.has(model.id);
          if (!isOwned) {
            console.log(`⚠️ Removing piece "${model.name}" from outfit "${outfit.name}" - no longer owned`);
            outfitsUpdated = true;
          }
          return isOwned;
        });

        // Only update if pieces were removed
        if (validModels.length !== outfit.models.length) {
          return {
            ...outfit,
            models: validModels,
            pieces: validModels.length
          };
        }

        return outfit;
      }).filter(outfit => {
        // Remove outfits that have no pieces left
        if (outfit.models && outfit.models.length === 0) {
          console.log(`🗑️ Removing empty outfit "${outfit.name}"`);
          outfitsUpdated = true;
          return false;
        }
        return true;
      });

      // Update localStorage and state if changes were made
      if (outfitsUpdated) {
        console.log('💾 Updating outfits after validation');
        localStorage.setItem('outfits', JSON.stringify(validatedOutfits));
        setOutfits(validatedOutfits);
      }
    } catch (error) {
      console.error('Error validating outfits:', error);
    }
  }, [ownedPieceIds, isConnected]); // Only run when ownedPieceIds changes

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Most Recent' },
    { id: 'name', name: 'Name A-Z' },
    { id: 'pieces', name: 'Most Pieces' },
    { id: 'favorites', name: 'Favorites' },
    { id: 'created', name: 'Date Created' }
  ];

  // Filter and sort outfits
  const filteredAndSortedOutfits = outfits
    .filter(outfit => {
      const matchesSearch = outfit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           outfit.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (outfit.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // If sorting by favorites, only show favorited items
      if (sortBy === 'favorites') {
        return matchesSearch && outfit.isFavorite;
      }
      
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'pieces':
          return b.pieces - a.pieces;
        case 'favorites':
          // Sort by recent date for favorites
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'recent':
        default:
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      }
    });

  // Handle outfit actions
  const handleCreateOutfit = () => {
    navigate('/outfit-builder');
  };

  const handleEditOutfit = (outfitId) => {
    navigate(`/outfit-builder?edit=${outfitId}`);
  };

  const handleViewOutfit = (outfitId) => {
    navigate(`/outfit-builder?view=${outfitId}`);
  };

  const handleDeleteOutfit = (outfitId) => {
    const outfit = outfits.find(o => o.id === outfitId);
    setDeleteConfirmModal({ 
      isOpen: true, 
      outfitId, 
      outfitName: outfit?.name || 'this outfit' 
    });
  };

  const handleDeleteConfirm = () => {
    const updatedOutfits = outfits.filter(outfit => outfit.id !== deleteConfirmModal.outfitId);
    setOutfits(updatedOutfits);
    localStorage.setItem('outfits', JSON.stringify(updatedOutfits));
    setDeleteConfirmModal({ isOpen: false, outfitId: null, outfitName: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ isOpen: false, outfitId: null, outfitName: '' });
  };

  const handleToggleFavorite = (outfitId) => {
    const updatedOutfits = outfits.map(outfit => 
      outfit.id === outfitId 
        ? { ...outfit, isFavorite: !outfit.isFavorite }
        : outfit
    );
    setOutfits(updatedOutfits);
    localStorage.setItem('outfits', JSON.stringify(updatedOutfits));
  };

  const handleShareOutfit = (outfit) => {
    // Mock share functionality
    const shareData = {
      title: outfit.name,
      text: outfit.description,
      url: `${window.location.origin}/outfit/${outfit.id}`
    };
    
    if (navigator.share) {
      navigator.share(shareData);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${outfit.name}: ${outfit.description}`);
      console.log('Outfit details copied to clipboard!'); // Note: Add notification system if needed
    }
  };

  const handleDownloadOutfit = async (outfit, event) => {
    event.stopPropagation(); // Prevent opening the modal
    
    if (!outfit || !outfit.models || outfit.models.length === 0) {
      setDownloadNotification({
        isVisible: true,
        message: 'This outfit has no pieces to download.',
        type: 'error'
      });
      setTimeout(() => setDownloadNotification({ isVisible: false, message: '', type: 'success' }), 3000);
      return;
    }

    try {
      console.log('========================================');
      console.log('Starting outfit download...', outfit);
      console.log(`Total models to load: ${outfit.models.length}`);
      console.log('Models:', outfit.models);
      console.log('========================================');

      // Default positions for different clothing categories (same as ModelManager)
      const CATEGORY_POSITIONS = {
        default: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        tops: { position: [0, 1.2, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        bottoms: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        shoes: { position: [0, 0.3, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        accessories: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [0.8, 0.8, 0.8] },
        hats: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [0.8, 0.8, 0.8] }
      };

      // Create a new Three.js scene to combine all models
      const combinedScene = new THREE.Scene();
      const loader = new GLTFLoader();
      let successCount = 0;
      let failCount = 0;

      // Load all models and add them to the scene with their transforms
      const loadPromises = outfit.models.map((model, index) => {
        return new Promise((resolve) => {
          const modelUrl = model.modelUrl;
          
          console.log(`[${index + 1}/${outfit.models.length}] Loading model:`, model.name);
          console.log(`  - URL: ${modelUrl}`);
          console.log(`  - Current Transform:`, model.currentTransform);
          
          if (!modelUrl) {
            console.warn(`  ❌ Model ${model.name} missing URL`);
            failCount++;
            resolve(null);
            return;
          }

          loader.load(
            modelUrl,
            (gltf) => {
              console.log(`  ✅ Successfully loaded ${model.name}`);
              
              // Clone the scene to avoid reference issues
              const loadedScene = gltf.scene.clone();
              
              // CRITICAL: Normalize the model just like ModelLoader does on the stage
              // Calculate bounding box (exact same process as ModelLoader.jsx lines 39-41)
              const box = new THREE.Box3().setFromObject(loadedScene);
              const center = box.getCenter(new THREE.Vector3());
              const size = box.getSize(new THREE.Vector3());
              
              console.log(`  📏 Model bounds:`, { 
                size: { x: size.x, y: size.y, z: size.z }, 
                center: { x: center.x, y: center.y, z: center.z } 
              });
              
              // Center the model (move it so its center is at origin) - ModelLoader.jsx line 62
              loadedScene.position.sub(center);
              
              // Calculate scale to fit within reasonable bounds (MUST match ModelLoader exactly)
              const maxDimension = Math.max(size.x, size.y, size.z);
              const targetSize = 2; // Target size in units (MUST match ModelLoader.jsx line 66)
              const normalizeScale = maxDimension > 0 ? targetSize / maxDimension : 1;
              
              console.log(`  📐 Normalization: maxDim=${maxDimension.toFixed(3)}, normalizeScale=${normalizeScale.toFixed(3)}`);
              
              // Apply normalization scale (exact same as ModelLoader.jsx line 70)
              loadedScene.scale.setScalar(normalizeScale);
              
              // Get the default position for this category
              let category = (model.category || 'default').toLowerCase();
              if (['hats', 'necklaces', 'belts', 'watches'].includes(category)) {
                category = 'accessories';
              }
              const defaultConfig = CATEGORY_POSITIONS[category] || CATEGORY_POSITIONS.default;
              
              // Apply the saved transforms from the outfit, or use defaults
              let position, rotation, scale;
              
              if (model.currentTransform) {
                position = model.currentTransform.position;
                rotation = model.currentTransform.rotation;
                scale = model.currentTransform.scale;
                console.log(`  📐 Applying saved transforms:`, { position, rotation, scale });
              } else {
                // Use default category positions
                position = defaultConfig.position;
                rotation = defaultConfig.rotation;
                scale = defaultConfig.scale;
                console.log(`  📐 Using default transforms for ${category}:`, { position, rotation, scale });
              }
              
              // Create a wrapper group to apply transforms
              // This matches how ModelLoader works with itemRootRef
              const wrapperGroup = new THREE.Group();
              wrapperGroup.add(loadedScene);
              wrapperGroup.name = model.name || `Piece_${index}`;
              
              // Apply position to wrapper
              if (position) {
                wrapperGroup.position.set(
                  position.x !== undefined ? position.x : (position[0] || 0),
                  position.y !== undefined ? position.y : (position[1] || 0),
                  position.z !== undefined ? position.z : (position[2] || 0)
                );
              }
              
              // Apply rotation to wrapper
              if (rotation) {
                wrapperGroup.rotation.set(
                  rotation.x !== undefined ? rotation.x : (rotation[0] || 0),
                  rotation.y !== undefined ? rotation.y : (rotation[1] || 0),
                  rotation.z !== undefined ? rotation.z : (rotation[2] || 0)
                );
              }
              
              // Apply scale to wrapper (this is in addition to normalization scale)
              if (scale) {
                wrapperGroup.scale.set(
                  scale.x !== undefined ? scale.x : (scale[0] || 1),
                  scale.y !== undefined ? scale.y : (scale[1] || 1),
                  scale.z !== undefined ? scale.z : (scale[2] || 1)
                );
              }
              
              combinedScene.add(wrapperGroup);
              successCount++;
              console.log(`  ✓ Added ${model.name} to combined scene at position:`, wrapperGroup.position);
              resolve(wrapperGroup);
            },
            (progress) => {
              // Loading progress
              if (progress.lengthComputable) {
                const percent = (progress.loaded / progress.total) * 100;
                console.log(`  Loading ${model.name}: ${percent.toFixed(0)}%`);
              }
            },
            (error) => {
              console.error(`  ❌ Failed to load ${model.name}:`, error);
              failCount++;
              resolve(null); // Resolve instead of reject to continue loading others
            }
          );
        });
      });

      // Wait for all models to load
      console.log('Waiting for all models to load...');
      const results = await Promise.all(loadPromises);
      
      console.log('========================================');
      console.log(`Loading complete! Success: ${successCount}, Failed: ${failCount}`);
      console.log(`Combined scene has ${combinedScene.children.length} children`);
      console.log('========================================');

      if (successCount === 0) {
        setDownloadNotification({
          isVisible: true,
          message: 'Failed to load any models. Please try again.',
          type: 'error'
        });
        setTimeout(() => setDownloadNotification({ isVisible: false, message: '', type: 'success' }), 3000);
        return;
      }

      // Export the combined scene as GLB
      console.log('Exporting combined scene as GLB...');
      const exporter = new GLTFExporter();
      
      exporter.parse(
        combinedScene,
        (result) => {
          console.log('✅ Export successful!');
          console.log(`File size: ${(result.byteLength / 1024 / 1024).toFixed(2)} MB`);
          
          // Create blob and download
          const blob = new Blob([result], { type: 'model/gltf-binary' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${outfit.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_outfit.glb`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          if (failCount > 0) {
            setDownloadNotification({
              isVisible: true,
              message: `Outfit downloaded successfully! All ${outfit.models.length} pieces included.`,
              type: 'success',
              details: `${successCount} of ${outfit.models.length} pieces included. ${failCount} piece(s) failed to load.`
            });
          } else {
            setDownloadNotification({
              isVisible: true,
              message: `Outfit downloaded successfully! All ${successCount} pieces included.`,
              type: 'success'
            });
          }
          setTimeout(() => setDownloadNotification({ isVisible: false, message: '', type: 'success' }), 4000);
        },
        (error) => {
          console.error('❌ Error exporting GLB:', error);
          setDownloadNotification({
            isVisible: true,
            message: 'Failed to export outfit. Please try again.',
            type: 'error'
          });
          setTimeout(() => setDownloadNotification({ isVisible: false, message: '', type: 'success' }), 3000);
        },
        { 
          binary: true, // Export as binary GLB
          onlyVisible: false,
          truncateDrawRange: false,
          includeCustomExtensions: true
        }
      );

    } catch (error) {
      console.error('❌ Error downloading outfit:', error);
      setDownloadNotification({
        isVisible: true,
        message: 'Failed to download outfit. Please try again.',
        type: 'error'
      });
      setTimeout(() => setDownloadNotification({ isVisible: false, message: '', type: 'success' }), 3000);
    }
  };

  // Handle dropdown
  const handleSortChange = (optionId) => {
    setSortBy(optionId);
    setIsDropdownOpen(false);
  };

  // Handle outfit detail modal
  const openOutfitDetailModal = (outfit) => {
    setOutfitDetailModal({ isOpen: true, outfit });
  };

  const closeOutfitDetailModal = () => {
    setOutfitDetailModal({ isOpen: false, outfit: null });
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <WalletRequired 
      message="You need to connect your wallet to view and manage your outfits."
      action="manage outfits"
    >
      <div className="min-h-screen bg-black">
        {/* Outfit Detail Modal */}
        <OutfitDetailModal
          isOpen={outfitDetailModal.isOpen}
          outfit={outfitDetailModal.outfit ? outfits.find(o => o.id === outfitDetailModal.outfit.id) : null}
        onClose={closeOutfitDetailModal}
        onToggleFavorite={handleToggleFavorite}
        onEdit={handleEditOutfit}
        onDelete={handleDeleteOutfit}
        onShare={handleShareOutfit}
        solPrice={solPrice}
      />

      <div className="max-w-full mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-white mb-2">My Outfits</h1>
          <p className="text-gray-400">
            Organize and manage your favorite outfit combinations created in the 3D wardrobe
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-black overflow-hidden">
          <div className="p-8">
            {/* Search and Filters */}
            <div className="mb-8">
              <div className="flex items-center justify-between gap-4">
                {/* Search Bar */}
                <div className="relative max-w-md flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search outfits..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                  />
                </div>
                
                {/* Create Outfit Button */}
                <button 
                  onClick={handleCreateOutfit}
                  className="flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-xl transition-all duration-200 font-medium hover:scale-105 shadow-lg border border-[#2a2a2a] hover:border-[#3a3a3a]"
                >
                  <Plus size={20} />
                  Create Outfit
                </button>
              </div>

              {/* Filters and Controls */}
              <div className="flex flex-wrap items-center gap-4 mt-6">
                {/* Sort Options */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-300">Sort by:</span>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center justify-between bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-white cursor-pointer min-w-[140px]"
                    >
                      <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                      <ChevronDown 
                        size={16} 
                        className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-2 w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl shadow-xl z-50 overflow-hidden">
                        {sortOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => handleSortChange(option.id)}
                            className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                              option.id === sortBy
                                ? 'bg-white/10 text-white'
                                : 'text-gray-300 hover:bg-white/5 hover:text-white'
                            }`}
                          >
                            <span>{option.name}</span>
                            {option.id === sortBy && (
                              <Check size={16} className="text-white" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Outfits Display */}
            {filteredAndSortedOutfits.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shirt size={40} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-medium text-gray-400 mb-2">
                  {outfits.length === 0 ? 'No outfits yet' : 'No outfits found'}
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {outfits.length === 0 
                    ? 'Start building your wardrobe by creating your first outfit in the 3D wardrobe builder'
                    : 'Try adjusting your search criteria or category filter'
                  }
                </p>
                {outfits.length === 0 && (
                  <button 
                    onClick={handleCreateOutfit}
                    className="flex items-center gap-2 px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium hover:scale-105 shadow-lg mx-auto"
                  >
                    <Plus size={20} />
                    Create Your First Outfit
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedOutfits.map((outfit) => (
                  <div
                    key={outfit.id}
                    className="group bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl overflow-hidden hover:border-white transition-all duration-200 hover:scale-105 cursor-pointer hover:shadow-xl hover:shadow-white/10"
                    onClick={() => openOutfitDetailModal(outfit)}
                  >
                    {/* Outfit Preview */}
                    <div className="relative w-full h-64 overflow-hidden">
                      <OutfitPreview 
                        outfit={outfit} 
                        className="w-full h-full"
                      />
                      <div className="absolute top-2 right-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(outfit.id);
                          }}
                          className="p-1.5 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                          title="Favorite outfit"
                        >
                          <Heart 
                            size={16} 
                            className={outfit.isFavorite ? 'text-red-500 fill-current' : 'text-white'} 
                          />
                        </button>
                      </div>

                    </div>

                    {/* Outfit Info */}
                    <div className="p-4 relative overflow-hidden">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-white text-lg mb-1">{outfit.name}</h3>
                          <p className="text-gray-400 text-sm line-clamp-2">{outfit.description}</p>
                        </div>
                      </div>
                      
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {outfit.tags?.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-[#2a2a2a] text-gray-200 text-xs rounded-full border border-[#4a4a4a]"
                            >
                              {tag}
                            </span>
                          ))}
                          {outfit.tags?.length > 3 && (
                            <span className="px-2 py-1 bg-[#2a2a2a] text-gray-400 text-xs rounded-full border border-[#4a4a4a]">
                              +{outfit.tags.length - 3}
                            </span>
                          )}
                        </div>

                      {/* Stats and Metadata */}
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4 text-gray-400">
                          <span className="flex items-center gap-1">
                            <img 
                              src="/images/piece-placeholder.png" 
                              alt="Piece"
                              className="w-6 h-6 object-contain"
                            />
                            {outfit.pieces || 0} {outfit.pieces === 1 ? 'piece' : 'pieces'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs">
                            {formatDate(outfit.updatedAt || outfit.createdAt)}
                          </span>
                          {outfit.rating && (
                            <div className="flex items-center gap-1">
                              <Star size={14} className="text-yellow-500 fill-current" />
                              <span className="text-gray-400 text-xs">{outfit.rating}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Hover Action Buttons - Slide up from bottom */}
                      <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out p-4 bg-gradient-to-t from-black/90 to-transparent flex justify-center gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOutfit(outfit.id);
                          }}
                          className="flex-1 bg-[#1a1a1a] hover:bg-[#2a2a2a] active:bg-[#0a0a0a] text-white font-medium py-2.5 px-4 rounded-lg border border-[#3a3a3a] hover:border-white/50 transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
                        >
                          <Eye size={16} />
                          <span>View on Stage</span>
                        </button>
                        <button
                          onClick={(e) => handleDownloadOutfit(outfit, e)}
                          className="flex-1 bg-[#1a1a1a] hover:bg-[#2a2a2a] active:bg-[#0a0a0a] text-white font-medium py-2.5 px-4 rounded-lg border border-[#3a3a3a] hover:border-white/50 transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
                        >
                          <Download size={16} />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Delete Confirmation Modal */}
    {deleteConfirmModal.isOpen && (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
        <div 
          className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
              <Trash2 size={24} className="text-red-400" />
            </div>
            
            <h3 className="text-xl font-semibold text-white mb-2">Delete Outfit</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete "{deleteConfirmModal.outfitName}"? This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleDeleteCancel}
                className="flex-1 px-4 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-300 hover:text-white rounded-xl transition-all duration-200 font-medium border border-[#2a2a2a] hover:border-[#3a3a3a]"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-red-500/25"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Download Notification */}
    {downloadNotification.isVisible && (
      <div className="fixed top-4 right-4 z-[70] transition-all duration-300 ease-out">
        <div className={`max-w-md rounded-xl shadow-2xl border ${
          downloadNotification.type === 'success' 
            ? 'bg-green-900/90 border-green-700 backdrop-blur-sm' 
            : 'bg-red-900/90 border-red-700 backdrop-blur-sm'
        } p-4 flex items-start gap-3`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            downloadNotification.type === 'success' 
              ? 'bg-green-500/20' 
              : 'bg-red-500/20'
          }`}>
            {downloadNotification.type === 'success' ? (
              <Check size={20} className="text-green-400" />
            ) : (
              <span className="text-red-400 font-bold text-xl">!</span>
            )}
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold mb-1">
              {downloadNotification.type === 'success' ? 'Success' : 'Error'}
            </h4>
            <p className="text-white/90 text-sm">
              {downloadNotification.message}
            </p>
            {downloadNotification.details && (
              <p className="text-white/70 text-xs mt-1">
                {downloadNotification.details}
              </p>
            )}
          </div>
          <button
            onClick={() => setDownloadNotification({ isVisible: false, message: '', type: 'success' })}
            className="flex-shrink-0 text-white/60 hover:text-white transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    )}

    </WalletRequired>
  );
};

export default Outfits;
